import { useState, useEffect } from "react"
import { motion } from "framer-motion";


const RadarDisplay = ({
  logs = [],
  onSignalClick,
}) => {
  const [sweepAngle, setSweepAngle] = useState(0)
  const [hoveredSignal, setHoveredSignal] = useState(null)

  // Convert logs to radar signals
  const convertLogsToSignals = (logs) => {
    return logs.map((log, index) => {
      // Generate angle based on index and timestamp for consistent positioning
      const angle = (index * (360 / logs.length) + Date.parse(log.timestamp) / 1000000) % (2 * Math.PI)

      // Calculate distance based on score (higher score = further from center)
      const distance = 60 + (log.score / 100) * 140 // Range from 60 to 200

      // Determine signal type based on signal_type
      let type = "low"
      if (log.score >= 85) {
        type = "critical"
      } else if (log.score >= 70 && log.score < 85) {
        type = "high"
      } else if (log.score >= 50 && log.score < 70) {
        type = "medium"
      }

      return {
        id: log.id,
        x: 250 + Math.cos(angle) * distance,
        y: 250 + Math.sin(angle) * distance,
        type,
        score: log.score,
        signal_type: log.signal_type.replace(/\*\*/g, ""), // Remove markdown formatting
        raw_signal: log.raw_signal,
        enrichment: log.enrichment,
        timestamp: log.timestamp,
        industry: log.industry,
        decision: log.decision,
        age: 0,
      }
    })
  }

  const [signals, setSignals] = useState([])

  // Update signals when logs change
  useEffect(() => {
    if (logs && logs.length > 0) {
      setSignals(convertLogsToSignals(logs))
    }
  }, [logs])

  // Radar sweep animation
  useEffect(() => {
    const interval = setInterval(() => {
      setSweepAngle((prev) => (prev + 2) % 360)
    }, 50)
    return () => clearInterval(interval)
  }, [])

  const getSignalFillColor = (type) => {
    switch (type) {
      case "critical":
        return "239, 68, 68" // red
      case "high":
        return "249, 115, 22" // orange
      case "medium":
        return "234, 179, 8" // yellow
      default:
        return "34, 197, 94" // green
    }
  }

  const getTagColor = (score) => {
    if (score >= 85) {
      return "text-red-400 bg-red-400/20"
    } else if (score >= 70) {
      return "text-orange-400 bg-orange-400/20"
    } else if (score >= 50) {
      return "text-yellow-400 bg-yellow-400/20"
    } else {
      return "text-green-400 bg-green-400/20"
    }
  }

  const getSignalColor = (score) => {
    if (score >= 85) {
      return "text-red-400"
    } else if (score >= 70) {
      return "text-orange-400"
    } else if (score >= 50) {
      return "text-yellow-400"
    } else {
      return "text-green-400"
    }
  }


  return (
    <div className="relative w-full h-full p-4 bg-white" >

      {/* SVG Radar Display */}
      <svg className="w-full h-full" viewBox="0 0 500 500">
        {/* Background circles with score indicators */}
        {[
          { radius: 100, score: "20" },
          { radius: 150, score: "50" },
          { radius: 200, score: "75" },
          { radius: 250, score: "100" },
        ].map(({ radius, score }, i) => (
          <g key={i}>
            <circle
              cx="250"
              cy="250"
              r={radius}
              fill="none"
              stroke="rgba(107, 114, 128, 0.4)"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text x={250 + radius - 10} y="255" className="fill-gray-500 text-xs opacity-60">
              {score}
            </text>
          </g>
        ))}

        {/* Cross lines */}
        <line x1="50" y1="250" x2="450" y2="250" stroke="rgba(107, 114, 128, 0.4)" strokeWidth="1" />
        <line x1="250" y1="50" x2="250" y2="450" stroke="rgba(107, 114, 128, 0.4)" strokeWidth="1" />

        {/* Sweep line */}
        <line
          x1="250"
          y1="250"
          x2={250 + Math.cos(((sweepAngle - 90) * Math.PI) / 180) * 200}
          y2={250 + Math.sin(((sweepAngle - 90) * Math.PI) / 180) * 200}
          stroke="rgba(34, 197, 94, 0.8)"
          strokeWidth="2"
          className="drop-shadow-lg"
        />

        {/* Signal points */}
        {signals.map((signal) => {
          const fillColor = getSignalFillColor(signal.type)
          const opacity = 0.8
          const isHighPriority = signal.type === "critical" || signal.score >= 85;
          const circleRadius = signal.score >= 85 ? "12" : signal.score >= 70 && signal.score < 85 ? "10" : signal.score >= 50 && signal.score < 70 ? "8" : "5";

          return (
            <g key={signal.id}>
              {/* Main signal dot */}
              <motion.circle
                cx={signal.x}
                cy={signal.y}
                r={circleRadius}
                fill={signal.signal_type.toLowerCase().includes('risk') ? "transparent" : `rgba(${fillColor}, ${opacity})`}
                stroke={signal.signal_type.toLowerCase().includes('risk') ? `rgba(${fillColor}, ${opacity})` : 'none'}
                strokeWidth="2"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredSignal(signal)}
                onMouseLeave={() => setHoveredSignal(null)}
                onClick={() => onSignalClick && onSignalClick(signal)} // Add onClick handler
                animate={{
                  opacity: [0.2, 1, 0.2],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Additional rings for high priority signals */}
              {isHighPriority && (
                <>
                  <circle
                    cx={signal.x}
                    cy={signal.y}
                    r="18"
                    fill="none"
                    stroke={`rgba(${fillColor}, 0.4)`}
                    strokeWidth="1"
                    className="animate-ping"
                    style={{
                      animationDuration: "2s",
                      animationDelay: "1s",
                    }}
                  />
                </>
              )}
            </g>
          )
        })}

        {/* Compass directions */}
        <text x="250" y="30" textAnchor="middle" className="fill-gray-600 text-xs opacity-80">
          N
        </text>
        <text x="470" y="255" textAnchor="middle" className="fill-gray-600 text-xs opacity-80">
          E
        </text>
        <text x="250" y="485" textAnchor="middle" className="fill-gray-600 text-xs opacity-80">
          S
        </text>
        <text x="30" y="255" textAnchor="middle" className="fill-gray-600 text-xs opacity-80">
          W
        </text>
      </svg>

      {hoveredSignal && (
        <div
          className="absolute z-50 bg-white border-2 border-gray-300 rounded-lg p-3 pointer-events-none min-w-[200px] shadow-xl"
          style={{
            top: "100%",
            left: "50%",
            transform: "translate(-50%, -70%)",
            pointerEvents: "none",
          }}
        >
          <div className="text-xs space-y-2">
            <div className="flex items-center justify-between">
              <span>Risk:</span>
              <span className={`px-2 py-1 rounded text-xs font-bold ${getSignalColor(hoveredSignal.signal_type)}`}>
                Tier 2 Churn Spike
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Tag:</span>
              <span className={`px-2 py-1 rounded text-xs ${getTagColor(hoveredSignal.score)}`}>
                {hoveredSignal.signal_type}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span >Score:</span>
              <span className={`px-2 py-1 rounded text-xs`}>
                {hoveredSignal.score}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Estimated Value Protected:</span>
              <span>
                {hoveredSignal?.estimatedValue || '$12.4K'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Auto-Execution:</span>
              <span className={hoveredSignal.autoExecution ? "text-green-400" : "text-red-400"}>
                {hoveredSignal.autoExecution ? "Triggered ✅" : "Disabled ❌"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default RadarDisplay
